import {
  MouseEvent as ReactMouseEvent,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';

import date from '@gem/services/date-service';
import { noop, throttle } from '@gem/utils';

import { IMediaPlayerProgressBuffer } from '../../hooks/useMediaPlayerProgress';

const ACTION_TO_KEY_CODE_MAP = {
  Back: 'ArrowLeft',
  Forward: 'ArrowRight',
};

const PROGRESS_SHIFT_SECONDS = 5;
const MIN_BUFFER_WITH_TO_DISPLAY = 0.1; // in percentage

const getProgressPercentage = (
  prevProgress: number,
  progress: number,
  progressMax: number,
  isUsePrediction: boolean
): number => {
  if (!isUsePrediction) {
    return (progress * 100) / progressMax;
  }

  const diffProgress = Math.abs(progress - prevProgress);
  const predictProgress = Math.min(progress + diffProgress, progressMax);
  return (predictProgress * 100) / progressMax;
};

const getBufferPercentage = (
  progressBuffer: IMediaPlayerProgressBuffer[],
  progressMax: number
): IMediaPlayerProgressBufferDisplay[] => {
  if (!progressMax || !Array.isArray(progressBuffer)) {
    return [];
  }

  return progressBuffer.reduce((result, item) => {
    const width = ((item.end - item.start) * 100) / progressMax;

    if (width >= MIN_BUFFER_WITH_TO_DISPLAY) {
      result.push({ start: (item.start * 100) / progressMax, width });
    }

    return result;
  }, [] as IMediaPlayerProgressBufferDisplay[]);
};

const getCursorPositionInTime = (
  event: MouseEvent | ReactMouseEvent,
  container: HTMLDivElement,
  progressMax: number
): number => {
  const { clientX } = event;
  const {
    x: progressLeft = 0,
    right: progressRight = 0,
    width: progressWidth = 0,
  } = container.getBoundingClientRect() || {};

  if (progressLeft >= clientX) {
    return 0;
  } else if (progressRight <= clientX) {
    return progressMax;
  } else {
    const percentage = ((clientX - progressLeft) * 100) / progressWidth;
    return (percentage / 100) * progressMax;
  }
};

interface IMediaPlayerProgressBufferDisplay {
  start: number;
  width: number;
}

interface IMediaPlayerProgressParams {
  isPlaying: boolean;
  progress: number;
  progressMax: number;
  isShowBuffer: boolean;
  progressBuffer: IMediaPlayerProgressBuffer[];
  isShowTooltip: boolean;
  onPlay(): void;
  onPause(): void;
  onProgress(progress: number): void;
  onProgressSelection(isSelected: boolean): void;
}

interface IMediaPlayerProgressPresenter {
  progressRef: MutableRefObject<HTMLDivElement | null>;
  progressPercentage: number;
  bufferPercentage: IMediaPlayerProgressBufferDisplay[];
  isChangingPosition: boolean;
  tooltipTime: string;
  isAnimatedProgress: boolean;
  onMouseDown(event: ReactMouseEvent): void;
}

type TUnsubscribeFunctions = () => void;

export function useMediaPlayerProgressPresenter(
  data: IMediaPlayerProgressParams
): IMediaPlayerProgressPresenter {
  const {
    isPlaying,
    progress,
    progressMax,
    isShowBuffer,
    progressBuffer,
    isShowTooltip,
    onPlay,
    onPause,
    onProgress,
    onProgressSelection,
  } = data;

  const progressRef = useRef<HTMLDivElement>(null);
  const unsubscribeFunctionsRef = useRef<TUnsubscribeFunctions>(noop);
  const [isChangingPosition, setChangingPosition] = useState<boolean>(false);
  const [isAnimatedProgress, setIsAnimatedProgress] = useState<boolean>(false);
  const [progressPercentage, setProgressPercentage] = useState<number>(0);
  const [bufferPercentage, setBufferPercentage] = useState<IMediaPlayerProgressBufferDisplay[]>([]);
  const isPlayingRef = useRef<boolean>(isPlaying);
  const shouldResumeVideoRef = useRef<boolean>(isPlaying);
  const progressCurrentRef = useRef<number>(progress);
  const [tooltipTime, setTooltipTime] = useState<string>('');

  const onMouseDown = useCallback(
    (event: ReactMouseEvent): void => {
      if (event.button !== 0) {
        return;
      }

      shouldResumeVideoRef.current = isPlayingRef.current;

      onPause();
      onMouseMove(event);
      setChangingPosition(true);
      onProgressSelection(true);

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);

      unsubscribeFunctionsRef.current = (): void => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
    },
    [progressMax, onPlay, onPause, onProgress]
  );

  const onMouseMove = useCallback(
    (event: MouseEvent | ReactMouseEvent): void => {
      const newProgress = progressRef.current
        ? getCursorPositionInTime(event, progressRef.current, progressMax)
        : 0;

      onProgress(newProgress);
    },
    [progressMax, onProgress]
  );

  const onMouseUp = useCallback((): void => {
    stopListening();

    if (shouldResumeVideoRef.current) {
      onPlay();
    }

    setChangingPosition(false);
    onProgressSelection(false);
  }, [onPlay]);

  const stopListening = useCallback((): void => {
    unsubscribeFunctionsRef.current && unsubscribeFunctionsRef.current();
  }, []);

  useLayoutEffect(() => {
    setIsAnimatedProgress(
      isPlaying && !isChangingPosition && progress >= progressCurrentRef.current
    );
  }, [progress, isPlaying, isChangingPosition]);

  useEffect(() => {
    setProgressPercentage(
      getProgressPercentage(progressCurrentRef.current, progress, progressMax, isAnimatedProgress)
    );
  }, [progress, progressMax, isAnimatedProgress]);

  useEffect(() => {
    if (isShowBuffer) {
      setBufferPercentage(getBufferPercentage(progressBuffer, progressMax));
    }
  }, [isShowBuffer, progressBuffer, progressMax]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => stopListening, []);

  useEffect(() => {
    progressCurrentRef.current = progress;
  }, [progress]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (progressRef.current !== document.activeElement) {
        return;
      }

      switch (event.key) {
        case ACTION_TO_KEY_CODE_MAP.Back: {
          const newProgress = progressCurrentRef.current - PROGRESS_SHIFT_SECONDS;
          onProgress(newProgress > 0 ? newProgress : 0);
          event.stopPropagation();
          break;
        }
        case ACTION_TO_KEY_CODE_MAP.Forward: {
          const newProgress = progressCurrentRef.current + PROGRESS_SHIFT_SECONDS;
          onProgress(newProgress > progressMax ? progressMax : newProgress);
          event.stopPropagation();
          break;
        }
      }
    };

    if (progressMax) {
      document.addEventListener('keydown', onKeyDown);
    }

    return (): void => {
      if (progressMax) {
        document.removeEventListener('keydown', onKeyDown);
      }
    };
  }, [progressMax]);

  useEffect(() => {
    const updateTime = throttle((event: Event) => {
      const time = progressRef.current
        ? getCursorPositionInTime(event as MouseEvent, progressRef.current, progressMax)
        : 0;

      setTooltipTime(date.secondsToString(time));
    }, 50);

    if (isShowTooltip) {
      progressRef.current?.addEventListener('mousemove', updateTime);
    }

    return () => {
      progressRef.current?.removeEventListener('mousemove', updateTime);
    };
  }, [isShowTooltip, progressMax]);

  return {
    progressRef,
    onMouseDown,
    progressPercentage,
    bufferPercentage,
    isChangingPosition,
    isAnimatedProgress,
    tooltipTime,
  };
}
